<script lang="ts" setup>
import type { NotificationInterface } from '@autobid/nuxt-pinia-store/types/Notification'
import { useAppStore } from '@autobid/nuxt-pinia-store/store/useAppStore'
import { useNotificationStore } from '@autobid/nuxt-pinia-store/store/useNotificationStore'
import {
  usePrefetchBarometerCar,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { useAuctionPagesLink } from '@autobid/ui/composables/useAuctionPagesLink'
import { useOpenLink } from '@autobid/ui/composables/useOpenLink'
import { storeToRefs } from 'pinia'
import NotificationsListElement from './NotificationsListElement.vue'

interface Props {
  notification: NotificationInterface
}

const props = defineProps<Props>()

const { loading } = storeToRefs(useAppStore())
const { APP_ID } = useRuntimeConfig().public
const { templates } = storeToRefs(useNotificationStore())
const { prefetchBarometerCar } = usePrefetchBarometerCar()
const { readNotification } = useNotificationStore()
const push = usePush()
const { t } = useI18n()
const { open } = useOpenLink()
const { getLink } = useAuctionPagesLink(Number(props.notification.car?.app_id))

async function markAsReadAndNavigate() {
  const template = templates.value[Number(props.notification.type)]

  if (Number(props.notification.status) === 0) {
    readNotification(Number(props.notification.id))
  }

  if (!template) {
    push.warning(t('header.notifications-center.unable-to-open-related-page'))
    return
  }

  if (props.notification.car?.app_id) {
    loading.value = true

    try {
      const data = await prefetchBarometerCar({
        type: 'item',
        carId: Number(props.notification.car.id),
      })

      if (!data?.item?.itemData)
        return

      const isSameApp = props.notification.car.app_id === String(APP_ID)
      if (isSameApp) {
        const barometerUrl = getLink({
          page: 'barometer',
          slug: data.item.itemData.slug,
        })

        barometerUrl && open(barometerUrl)
      }
      else if (props.notification.car.path) {
        window.location.href = props.notification.car.path
      }
    }
    catch (error) {
      if (isNuxtError(error)) {
        if (error.statusCode === 404) {
          push.warning(t('header.notifications-center.the-car-does-not-exist'))
        }
        else {
          push.error(
            error.statusMessage
            || t(`error-page.${error.statusCode}.description`),
          )
        }
      }
      else {
        push.error(t('unexpected-error'))
      }
    }

    loading.value = false
  }
  else {
    let link = template.link[APP_ID] || template.link.default || ''

    if (link.includes('{car.path}') && !props.notification.car) {
      push.warning(t('header.notifications-center.the-car-does-not-exist'))
    }
    else if (link.length) {
      const replaceMap = {
        '{id}': props.notification.id,
        '{car.path}': props.notification.car?.path,
      }

      for (const key in replaceMap) {
        link = link.replace(key, replaceMap[key as keyof typeof replaceMap] || '')
      }

      open(link, true)
    }
  }
}
</script>

<template>
  <NotificationsListElement
    :unread="Number(notification.status) === 0"
    @click="markAsReadAndNavigate"
  >
    <slot />
  </NotificationsListElement>
</template>
